import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, Star, MapPin, Sparkles, ChevronDown, Quote } from 'lucide-react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { useSiteContent } from '../context/SiteContentContext';
import { settingOf, settingNameOf, isSignature } from '../lib/roomSettings';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Normalize backend-relative URLs (e.g. /api/uploads/...) to absolute URLs
const normalizeUrl = (url) =>
  url?.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;

const HERO_IMAGES = [
  "/images/hero-gate.jpg",
  "/images/resort-pool-night.jpg",
];

// Plays first in the hero, then the photo carousel takes over. Vertical cut for mobile.
const HERO_VIDEO_DESKTOP = "/media/rudi-hero.mp4";
const HERO_VIDEO_MOBILE = "/media/rudi-hero-mobile.mp4";

// The story of the land, told in five chapters. Setting scenes pull real room
// photography from the DB; fallbacks are known-good URLs already in use on the site.
const STORY_SCENES = [
  {
    id: 'sand',
    overline: 'The Sand',
    title: <>It begins with the <span className="text-gold-500">dunes</span></>,
    text: "Private sand dunes inside the resort boundary — take an ATV up them, ride a horse across them, or simply sit and watch the light change. No crowds, no queues. This stretch of the Thar is yours for the evening.",
    link: '/experiences',
    linkLabel: 'Desert Adventures',
    fallback: '/images/story-sand.jpeg',
  },
  {
    id: 'water',
    setting: 'water',
    overline: 'The Water',
    title: <>Then, impossibly, <span className="text-gold-500">water</span></>,
    text: "A natural pond and waterfall sit at the centre of the property — the sound of falling water in a land defined by its absence. The Cascade Cottages open straight onto it.",
    link: '/stay?setting=water',
    linkLabel: 'Stay by the Water',
    fallback: '/images/resort-pond.jpg',
  },
  {
    id: 'stone',
    setting: 'caves',
    preferSlug: 'catacomb-suite',
    overline: 'The Stone',
    title: <>Cooler, darker, <span className="text-gold-500">older</span></>,
    text: "On the cave side of the property, rooms are set against rock that stays naturally cool through the desert day. The Heritage Huts and the Catacomb Suite are built into this quarter — quiet, private, unlike anywhere you've slept.",
    link: '/stay?setting=caves',
    linkLabel: 'Stay in the Stone',
    fallback: '/images/resort-cave.jpg',
  },
  {
    id: 'green',
    // no `setting`: the garden rooms' galleries are all interiors, so the
    // self-hosted lawn shot below is better than any DB photo here
    overline: 'The Green',
    title: <>A garden in the <span className="text-gold-500">Thar</span></>,
    text: "Around the greenhouse lies the resort's soft corner — gardens, cycling and jogging tracks, and the kids' play zone. The Cocoon and the Blue Enclave sit among the green.",
    link: '/stay?setting=garden',
    linkLabel: 'Stay in the Green',
    fallback: '/images/resort-lawn.jpg',
  },
  {
    id: 'night',
    overline: 'The Night',
    title: <>The desert saves its best for <span className="text-gold-500">last</span></>,
    text: "Dinner from the live open kitchen, cultural programs on the open-air stage, the rain dance floor, a drink at the bar — and above it all, a desert sky with no city lights to dim it.",
    link: '/experiences',
    linkLabel: 'Plan Your Evenings',
    fallback: '/images/resort-cabanas-night.jpg',
  },
];

const TESTIMONIALS = [
  {
    name: "Priya & Arjun Sharma",
    location: "Mumbai",
    text: "The Catacomb Suite was unlike anything we've experienced anywhere in India. Waking up to the sound of wind over the dunes, with a private waterfall just steps away — it felt like another world. The team anticipated every need without us having to ask.",
    rating: 5,
    occasion: "Honeymoon"
  },
  {
    name: "Ramesh Gupta",
    location: "Delhi",
    text: "Brought my family of six for a long weekend. The Heritage Huts gave us enough space, and the kids were completely absorbed — ATV rides, archery, the rain dance. The food in the open kitchen was exceptional, especially the dal baati churma.",
    rating: 5,
    occasion: "Family Trip"
  },
  {
    name: "Sneha Iyer",
    location: "Bangalore",
    text: "We celebrated our anniversary here and couldn't have chosen better. The desert at night, lit only by stars — it's something you can't photograph, only live. The staff arranged a candlelit dinner on our private dune. Truly memorable.",
    rating: 5,
    occasion: "Anniversary"
  },
];

const HOME_FAQS = [
  {
    q: "How far is Rudragarh from Jaisalmer city?",
    a: "Rudragarh Resort is in Damodra village on Sam Road, about 21 km from Jaisalmer city, the railway station, and the airport — a 25–30 minute drive. We can arrange pick-up from the station or airport on request."
  },
  {
    q: "Are all resort facilities included in the room rate?",
    a: "Yes — swimming pool, gym, dining hall, bar, access to private sand dunes, horse riding, ATV rides, archery, rain dance, cultural programs, open-air theatre, kids' play zone, and more are all included for every guest staying at the resort."
  },
  {
    q: "What is the best time to visit Rudragarh Resort?",
    a: "October to March is the ideal season when the desert is cool and pleasant. The Desert Festival (typically in February) coincides with the most vibrant cultural atmosphere. Summer months (April–June) are very hot but the resort remains operational."
  },
  {
    q: "Can I book a cave-side stay for a group?",
    a: "The Catacomb Suite accommodates up to 4 guests per unit with 2 double beds, and The Heritage Huts sleep 4 each. For larger groups, we recommend combining them with the Cascade Cottages by the pond. Contact us to arrange a customised group package."
  },
];

const Home = () => {
  const { siteContent } = useSiteContent();
  const [rooms, setRooms] = useState([]);
  const [currentHeroIndex, setCurrentHeroIndex] = useState(0);
  const [openFaq, setOpenFaq] = useState(null);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches);
  const videoRef = useRef(null);

  const heroImages = (siteContent?.homepage?.hero_images?.length > 0)
    ? siteContent.homepage.hero_images.map(normalizeUrl)
    : HERO_IMAGES;
  // Video first (vertical cut on mobile, horizontal on desktop), then the photos.
  const heroVideoSrc = isMobile ? HERO_VIDEO_MOBILE : HERO_VIDEO_DESKTOP;
  const heroSlides = [{ type: 'video', src: heroVideoSrc }, ...heroImages.map((src) => ({ type: 'image', src }))];
  const heroTitle = siteContent?.homepage?.hero_title || 'Rudragarh';
  const heroSubtitle = siteContent?.homepage?.hero_subtitle || 'A Legacy of Grace';
  const heroDescription = siteContent?.homepage?.hero_description || 'A Luxury Adventure Retreat in the heart of the Thar Desert. Where rugged exploration meets high-end hospitality.';
  const testimonials = (siteContent?.testimonials?.length > 0) ? siteContent.testimonials : TESTIMONIALS;
  const homeFaqs = (siteContent?.homepage_faqs?.length > 0) ? siteContent.homepage_faqs : HOME_FAQS;

  const signatureRooms = rooms
    .filter(isSignature)
    .sort((a, b) => b.price_per_night - a.price_per_night);

  // First real room photo from a setting — story scenes prefer actual property photography.
  // preferSlug pins a specific room (e.g. the carved cave suite for "The Stone").
  const sceneImage = (settingId, preferSlug) => {
    const pool = rooms.filter((r) => settingOf(r) === settingId && r.images?.length > 0);
    const room = pool.find((r) => r.slug === preferSlug) || pool[0];
    return room ? normalizeUrl(room.images[0]) : null;
  };

  useEffect(() => {
    fetchRooms();
  }, []);

  // Swap hero video (horizontal ↔ vertical) when the viewport crosses the mobile breakpoint.
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  // Photos advance every 5s; the video slide advances on its own `onEnded`.
  useEffect(() => {
    if (heroSlides.length <= 1) return;
    if (heroSlides[currentHeroIndex]?.type === 'video') return;
    const t = setTimeout(() => {
      setCurrentHeroIndex((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearTimeout(t);
  }, [currentHeroIndex, heroSlides.length]);

  // Play the hero video from the start whenever its slide is active; pause otherwise (so it replays each loop).
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (heroSlides[currentHeroIndex]?.type === 'video') {
      v.currentTime = 0;
      v.play().catch(() => {});
    } else {
      v.pause();
    }
  }, [currentHeroIndex, heroSlides.length]);

  const fetchRooms = async () => {
    try {
      const response = await axios.get(`${API}/rooms`);
      setRooms(response.data);
    } catch (error) {
      console.error('Error fetching rooms:', error);
    }
  };

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Rudragarh Resort | Boutique Desert Stay near Jaisalmer, Rajasthan</title>
        <meta name="description" content="Rudragarh Resort — a boutique desert retreat near Jaisalmer. Cave-side stays, pond-side cottages, private dunes, pool, and unforgettable desert experiences." />
        <meta property="og:title" content="Rudragarh Resort | Boutique Desert Stay near Jaisalmer" />
        <meta property="og:description" content="Escape to Rudragarh — pond-side cottages, cave-side stays, private sand dunes, and the golden desert of Rajasthan." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.rudragarh.com/" />
        <meta property="og:image" content="https://www.rudragarh.com/images/resort-pool-night.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Rudragarh Resort | Boutique Desert Stay near Jaisalmer" />
        <meta name="twitter:description" content="Escape to Rudragarh — pond-side cottages, cave-side stays, private sand dunes, and the golden desert of Rajasthan." />
        <meta name="twitter:image" content="https://www.rudragarh.com/images/resort-pool-night.jpg" />
        <link rel="canonical" href="https://www.rudragarh.com/" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LodgingBusiness",
          "name": "Rudragarh Resort",
          "alternateName": "Rudragarh Resort & Hotel | A Legacy of Grace",
          "description": "Boutique desert retreat in Damodra on Sam Road, Jaisalmer offering cave-side stays, pond-side cottages, private dunes, and luxury desert experiences.",
          "url": "https://www.rudragarh.com",
          "telephone": "+91 9649611111",
          "email": "info@rudragarh.com",
          "hasMap": "https://share.google/JDO0fSPxDmYuYY1vX",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Village Damodra, Sam Road",
            "addressLocality": "Jaisalmer",
            "addressRegion": "Rajasthan",
            "postalCode": "345001",
            "addressCountry": "IN"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": "26.9052",
            "longitude": "70.7193"
          },
          "image": "https://www.rudragarh.com/images/resort-pool-night.jpg",
          "checkinTime": "11:00",
          "checkoutTime": "10:00",
          "amenityFeature": [
            {"@type": "LocationFeatureSpecification", "name": "Swimming Pool", "value": true},
            {"@type": "LocationFeatureSpecification", "name": "Private Sand Dunes", "value": true},
            {"@type": "LocationFeatureSpecification", "name": "Restaurant", "value": true},
            {"@type": "LocationFeatureSpecification", "name": "Bar", "value": true},
            {"@type": "LocationFeatureSpecification", "name": "Horse Riding", "value": true},
            {"@type": "LocationFeatureSpecification", "name": "Gym", "value": true},
            {"@type": "LocationFeatureSpecification", "name": "ATV Rides", "value": true},
            {"@type": "LocationFeatureSpecification", "name": "Camel Safari", "value": true},
            {"@type": "LocationFeatureSpecification", "name": "Open-Air Theatre", "value": true}
          ],
          "priceRange": "₹₹₹",
          "aggregateRating": {"@type": "AggregateRating", "ratingValue": "5.0", "reviewCount": "33"}
        })}</script>
      </Helmet>
      {/* Hero Section */}
      <section className="relative h-screen" data-testid="hero-section">
        {/* Background Images */}
        {heroSlides.map((slide, index) => (
          <div
            key={slide.src}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentHeroIndex ? 'opacity-100' : 'opacity-0'
            }`}
          >
            {slide.type === 'video' ? (
              <video
                ref={videoRef}
                src={slide.src}
                muted
                playsInline
                autoPlay
                preload="auto"
                onEnded={() => setCurrentHeroIndex((prev) => (prev + 1) % heroSlides.length)}
                className="w-full h-full object-cover"
              />
            ) : (
              <img
                src={slide.src}
                alt="Rudragarh Resort"
                className="w-full h-full object-cover"
                style={{ objectPosition: '50% 62%' }}
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-b from-void/60 via-void/40 to-void" />
          </div>
        ))}

        {/* Hero Content */}
        <div className="relative z-10 h-full flex items-center justify-center text-center px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <span className="overline">Welcome to</span>
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-heading font-medium tracking-tight mb-6 text-shadow-gold">
              <span className="text-gradient-gold">{heroTitle}</span>
            </h1>
            <p className="font-accent text-2xl md:text-3xl text-gold-400 mb-4">
              {heroSubtitle}
            </p>
            <p className="text-lg md:text-xl text-foreground/70 max-w-2xl mx-auto mb-10 font-body">
              {heroDescription}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/book" className="btn-primary" data-testid="hero-book-btn">
                Book Your Stay
              </Link>
              <Link to="/stay" className="btn-outline" data-testid="hero-explore-btn">
                Explore Rooms
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <div className="w-6 h-10 border-2 border-gold-500/50 rounded-full flex justify-center pt-2">
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ repeat: Infinity, duration: 1.5 }}
              className="w-1.5 h-1.5 bg-gold-500 rounded-full"
            />
          </div>
        </motion.div>
      </section>

      {/* Announcement strip — self-expiring: event line hides after 31 Oct, offer
          link hides once admin clears original_price (or after 2 Nov). */}
      {(() => {
        const eventLive = new Date() < new Date('2026-11-02T00:00:00+05:30');
        const offerLive = new Date() < new Date('2026-10-28T00:00:00+05:30')
          && rooms.some((r) => r.original_price > r.price_per_night);
        if (!eventLive && !offerLive) return null;
        return (
          <section className="bg-gold-500/10 border-y border-gold-500/20" data-testid="announcement-strip">
            <div className="max-w-7xl mx-auto container-padding py-3 flex flex-col sm:flex-row items-center justify-center gap-1.5 sm:gap-6 text-center text-sm">
              {eventLive && (
                <p className="text-foreground/80">
                  <span className="text-gold-400 font-medium">28 Oct – 1 Nov:</span> the entire resort is reserved for a Bikers&apos; Event — more details soon.
                </p>
              )}
              {offerLive && (
                <Link to="/stay" className="text-gold-500 hover:text-gold-400 whitespace-nowrap font-medium">
                  Festive Offer — up to 40% off till 27 Oct →
                </Link>
              )}
            </div>
          </section>
        );
      })()}

      {/* Why Rudragarh — the manifesto */}
      <section className="section-padding bg-void-paper" data-testid="why-rudragarh-section">
        <div className="max-w-3xl mx-auto container-padding text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <span className="overline">Why Rudragarh</span>
            <h2 className="text-4xl md:text-5xl font-heading font-medium">
              A Fort of the <span className="text-gold-500">Elements</span>
            </h2>
            <p className="text-foreground/70 leading-relaxed text-lg">
              A garh is a fort. Rudragarh gathers the Thar's rarest elements
              inside one boundary: open sand dunes, a natural pond that falls
              into a waterfall, rooms set against cave rock, and a greenhouse
              keeping a corner of the desert green.
            </p>
            <p className="font-accent text-2xl text-gold-400">
              You don't pick a room here — you pick where in the story you sleep.
            </p>
          </motion.div>
        </div>
      </section>

      {/* The Story — five chapters of the land */}
      <section data-testid="story-scenes">
        {STORY_SCENES.map((scene, i) => (
          <div key={scene.id} className={i % 2 === 1 ? 'bg-void-paper' : ''}>
            <div className="max-w-7xl mx-auto container-padding py-16 md:py-24">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                <motion.div
                  initial={{ opacity: 0, x: i % 2 === 1 ? 50 : -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className={`lg:col-span-7 ${i % 2 === 1 ? 'lg:order-2' : ''}`}
                >
                  <img
                    src={(scene.setting && sceneImage(scene.setting, scene.preferSlug)) || scene.fallback}
                    alt={scene.overline}
                    className="w-full h-[380px] md:h-[500px] object-cover"
                    loading="lazy"
                  />
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="lg:col-span-5 space-y-5"
                >
                  <span className="overline">Chapter 0{i + 1} — {scene.overline}</span>
                  <h3 className="text-3xl md:text-4xl font-heading font-medium">
                    {scene.title}
                  </h3>
                  <p className="text-foreground/70 leading-relaxed">{scene.text}</p>
                  <Link
                    to={scene.link}
                    className="btn-outline inline-block w-fit"
                    data-testid={`story-cta-${scene.id}`}
                  >
                    {scene.linkLabel}
                  </Link>
                </motion.div>
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* Signature Stays */}
      <section className="section-padding" data-testid="featured-rooms-section">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="text-center mb-16">
            <span className="overline">Accommodations</span>
            <h2 className="text-4xl md:text-5xl font-heading font-medium">
              Signature <span className="text-gold-500">Stays</span>
            </h2>
            <p className="text-foreground/60 mt-4 max-w-2xl mx-auto">
              Every stay at Rudragarh belongs to a chapter of the land —
              by the water, into the stone, at the heart of the resort, or among the green.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {signatureRooms.map((room, index) => (
              <motion.div
                key={room.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="room-card group"
              >
                <Link to={`/stay/${room.slug}`} data-testid={`room-card-${room.slug}`}>
                  <div className="relative h-80 overflow-hidden">
                    <img
                      src={normalizeUrl(room.images[0])}
                      alt={room.name}
                      className="room-card-image w-full h-full object-cover transition-transform duration-700"
                    />
                    <div className="room-card-overlay absolute inset-0 bg-gradient-to-t from-void via-transparent to-transparent opacity-60 transition-opacity duration-300" />
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <span className="text-gold-500 text-xs uppercase tracking-widest">
                        {settingNameOf(room)}
                      </span>
                      <h3 className="font-heading text-2xl mt-2">{room.name}</h3>
                      <p className="text-foreground/70 text-sm mt-2 line-clamp-2">
                        {room.short_description}
                      </p>
                      <div className="flex items-center justify-between mt-4">
                        <span className="text-gold-500 font-heading text-xl">
                          {room.original_price > room.price_per_night && (
                            <span className="text-foreground/40 line-through text-sm mr-1.5">₹{room.original_price.toLocaleString()}</span>
                          )}
                          ₹{room.price_per_night.toLocaleString()}
                          {room.original_price > room.price_per_night && (
                            <span className="text-[10px] uppercase tracking-wider bg-gold-500/20 text-gold-400 px-1.5 py-0.5 rounded ml-2 align-middle whitespace-nowrap">
                              {Math.round((1 - room.price_per_night / room.original_price) * 100)}% off
                            </span>
                          )}
                        </span>
                        <span className="text-foreground/50 text-sm">per night</span>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/stay" className="btn-outline" data-testid="view-all-rooms-btn">
              View All Stays
            </Link>
          </div>
        </div>
      </section>

      {/* USP Section */}
      <section className="section-padding bg-void-paper" data-testid="usp-section">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <span className="overline">The Experience</span>
              <h2 className="text-4xl md:text-5xl font-heading font-medium">
                {siteContent?.homepage?.usp_title
                  ? <span className="text-gold-500">{siteContent.homepage.usp_title}</span>
                  : <>Earthy. Exclusive. <span className="text-gold-500">Experiential.</span></>
                }
              </h2>
              <p className="text-foreground/70 leading-relaxed">
                {siteContent?.homepage?.usp_description || 'Rudragarh offers an unparalleled escape into the golden landscapes of Rajasthan. Our resort combines the raw beauty of the Thar Desert with luxurious comfort, creating memories that last a lifetime.'}
              </p>
              <div className="grid grid-cols-2 gap-6 pt-6">
                {[
                  { icon: <Sparkles size={24} />, label: "Cave Stay", desc: "Unique subterranean luxury" },
                  { icon: <MapPin size={24} />, label: "Private Dunes", desc: "Exclusive desert access" },
                  { icon: <Star size={24} />, label: "Waterfall", desc: "Natural desert oasis" },
                  { icon: <ChevronRight size={24} />, label: "Adventure", desc: "ATVs, horses & more" },
                ].map((item, index) => (
                  <div key={index} className="space-y-2">
                    <div className="text-gold-500">{item.icon}</div>
                    <h4 className="font-heading text-lg">{item.label}</h4>
                    <p className="text-foreground/50 text-sm">{item.desc}</p>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src={siteContent?.homepage?.usp_image ? normalizeUrl(siteContent.homepage.usp_image) : '/images/resort-cave.jpg'}
                alt="Cave Suite"
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-gold-500 text-black p-6">
                <p className="font-heading text-lg">{siteContent?.homepage?.usp_image_caption || 'The Catacomb Suite'}</p>
                <p className="text-sm opacity-80">{siteContent?.homepage?.usp_image_subcaption || 'One-of-a-kind experience'}</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="text-center mb-16">
            <span className="overline">Guest Stories</span>
            <h2 className="text-4xl md:text-5xl font-heading font-medium">
              What Our <span className="text-gold-500">Guests</span> Say
            </h2>
            <a
              href="https://share.google/JDO0fSPxDmYuYY1vX"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-6 px-5 py-2 border border-gold-500/30 text-sm hover:border-gold-500 transition-colors"
              data-testid="google-rating-link"
            >
              <span className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={14} className="text-gold-500 fill-gold-500" />
                ))}
              </span>
              <span className="text-foreground/80">5.0 on Google Reviews</span>
            </a>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-void-subtle border border-gold-500/10 p-8 space-y-5"
              >
                <Quote size={28} className="text-gold-500/40" />
                <p className="text-foreground/70 leading-relaxed text-sm">{t.text}</p>
                <div className="flex items-center gap-1 pt-2">
                  {Array.from({ length: t.rating }).map((_, j) => (
                    <Star key={j} size={14} className="text-gold-500 fill-gold-500" />
                  ))}
                </div>
                <div className="border-t border-gold-500/10 pt-4">
                  <p className="font-heading text-base">{t.name}</p>
                  <p className="text-foreground/40 text-xs uppercase tracking-widest mt-1">
                    {t.occasion} · {t.location}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding bg-void-paper" data-testid="home-faq-section">
        <div className="max-w-3xl mx-auto container-padding">
          <div className="text-center mb-12">
            <span className="overline">Questions</span>
            <h2 className="text-4xl font-heading font-medium">
              Frequently <span className="text-gold-500">Asked</span>
            </h2>
          </div>
          <div className="space-y-3">
            {homeFaqs.map((faq, i) => (
              <div key={i} className="bg-void-subtle border border-gold-500/10">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-6 text-left"
                >
                  <span className="font-heading text-lg pr-4">{faq.q}</span>
                  <ChevronDown
                    size={18}
                    className={`text-gold-500 flex-shrink-0 transition-transform duration-200 ${openFaq === i ? 'rotate-180' : ''}`}
                  />
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-6 text-foreground/70 leading-relaxed border-t border-gold-500/10 pt-4">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-32" data-testid="cta-section">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('/images/resort-hut-night.jpg')`,
          }}
        />
        <div className="absolute inset-0 bg-void/80" />
        <div className="relative z-10 max-w-4xl mx-auto text-center px-6">
          <span className="overline">Your Chapter Awaits</span>
          <h2 className="text-4xl md:text-5xl font-heading font-medium mb-6">
            Choose Where Your <span className="text-gold-500">Story</span> Begins
          </h2>
          <p className="text-foreground/70 mb-10 max-w-2xl mx-auto">
            Sand, water, stone or green — book your stay at Rudragarh
            and sleep inside the story of the Thar.
          </p>
          <Link to="/book" className="btn-primary" data-testid="cta-book-btn">
            Reserve Your Experience
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
